/* =============================================================================
   Cart participants – with localStorage fallback (14‑day TTL)
   ============================================================================= */

/* -----------------------------------------------------------------------------
   light‑weight localStorage helper – safely handles unavailable storage
  keyed by variant and stable delegate identity, not cart position
----------------------------------------------------------------------------- */
const STORAGE_KEY = 'cartParticipantsCacheV2';
const CACHE_TTL   = 14 * 24 * 60 * 60 * 1000; // 14 days → ms

function _canStore () {
  try {
    const test = '__ls_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (_) {
    return false;
  }
}

function loadCache () {
  if (!_canStore()) return {};
  try {
    const cache = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return cache && typeof cache === 'object' && !Array.isArray(cache) ? cache : {};
  } catch (_) {
    return {};
  }
}

function saveCache (obj) {
  if (!_canStore()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (_) {}
}

function _delegateKey (line, delegate) {
  return `${line}_${delegate}`;
}

function cleanupCache () {
  const o = loadCache();
  const now = Date.now();
  let dirty = false;
  for (const [k, v] of Object.entries(o)) {
    if (!v || !v.timestamp || !v.values || typeof v.values !== 'object' || now - v.timestamp > CACHE_TTL) {
      delete o[k];
      dirty = true;
    }
  }
  if (dirty) saveCache(o);
}

function getDelegateCache (line, delegate) {
  cleanupCache();
  const o = loadCache();
  return o[_delegateKey(line, delegate)]?.values || null;
}

function setFieldCache (line, delegate, name, value) {
  if (!_canStore()) return;
  const o   = loadCache();
  const key = _delegateKey(line, delegate);
  o[key] = o[key] || { timestamp: Date.now(), values: {} };
  o[key].values[name] = value;
  o[key].timestamp    = Date.now();
  saveCache(o);
}

function removeDelegateCache (line, delegate) {
  if (!_canStore()) return;
  const o   = loadCache();
  const key = _delegateKey(line, delegate);
  if (o[key]) {
    delete o[key];
    saveCache(o);
  }
}

function clearAllCache () {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

cleanupCache();

/* =========================
   cart-participant-row
   ========================= */
if (!customElements.get('cart-participant-row')) {
  customElements.define('cart-participant-row', class CartParticipantRow extends HTMLElement {
    constructor () {
      super();
      this.processing = false;

      /* ----- quick DOM look‑ups ----- */
      this.selectors = {
        properties : this?.querySelector('[name="properties"]')?.textContent,
        remove     : this?.querySelector('[data-button="remove"]'),
        add        : this?.querySelector('[data-button="add"]'),
        fields     : this?.querySelectorAll('input[name], select[name], textarea[name]:not([name="properties"])'),
        fileRemove : this?.querySelector('.file-remove'),
        available  : this?.querySelector('[available-for-sale="false"]'),
        cartItem   : this?.closest('.cart-item')
      };
      this.requiredFileFields = new Set(Array.from(this.selectors.fields || [])
        .filter(field => field.type === 'file' && field.required).map(field => field.name));

      /* ----- immutable data coming from Liquid ----- */
      this.data = {
        handle    : this.dataset.productHandle,
        variant   : this.dataset.variantId,
        key       : this.dataset.rowKey,         // legacy
        quantity  : parseInt(this.dataset.quantity, 10),
        delegate  : this.dataset.delegateIndex,
        rowIndex  : parseInt(this.dataset.rowIndex, 10),
        line      : parseInt(this.dataset.lineIndex, 10),   // line index within cart
        properties: JSON.parse(this.selectors.properties || '[]').filter(([k]) => k !== 'properties')
      };
      this.data.cacheId = this.dataset.cacheId;

      /* preload any cached values */
      this.prefillFromCache();

      /* instant removal if no longer available */
      if (this.selectors.available && this.selectors.available.getAttribute('available-for-sale') === 'false') {
        fetch(`${routes.cart_change_url}`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ quantity: 0, id: this.data.variant, sections: 'default-cart' })
        }).then(() => window.location.reload());
        this.selectors.cartItem?.remove();
      }
    }

    /* ================= lifecycle ================= */
    connectedCallback () {
      this.selectors.remove?.addEventListener('click', this.delete.bind(this));
      console.log('cart-participant-row', this.data);
      this.selectors.add?.addEventListener('click', this.add.bind(this));

      this.selectors.fields?.forEach(f => {
        f.addEventListener('input', this.update.bind(this));
        f.addEventListener('change', this.update.bind(this));
      });

      this.selectors.fileRemove?.addEventListener('click', e => {
        e.preventDefault();
        const fileInput = this.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
          this.update({ target: fileInput, type: 'remove-file' });
        }
      });
    }

    /* expose flush so parent can sync all edits before submit */
    flush () {
      const tasks = Array.from(this.selectors.fields || [])
      .map(f => this.update({ target: f }))
      .filter(Boolean);                  // undefined for <input type=file> w/out value
      return Promise.all(tasks);
    }

    /* preload cached values (skips <input type="file">) */
    prefillFromCache () {
      const cache = getDelegateCache(this.data.variant, this.data.cacheId) || {};
      this.selectors.fields?.forEach(f => {
        if (Object.prototype.hasOwnProperty.call(cache, f.name)) {
          this.setProperty(f.name, cache[f.name]);
          if (f.type === 'file') return;
          if (f.type === 'checkbox') f.checked = cache[f.name] === true || cache[f.name] === 'true';
          else f.value = cache[f.name];
        } else if (f.type === 'checkbox') {
          const value = this.data.properties.find(([name]) => name === f.name)?.[1];
          f.checked = value === true || value === 'true';
        } else if (f.type === 'file') {
          const value = this.data.properties.find(([name]) => name === f.name)?.[1];
          if (value) f.required = false;
        }
      });
    }

    /* ================= validation ================= */
    validateInput (name, value) {
      switch (name) {
        case '_phone':
          //return /^((\+44\s?(\(0\)\s?)?)|0)(?:\d\s?){9,10}$/.test(value);
          // Accept UK and international numbers, allow +, spaces, dashes, parentheses, min 8 digits
          return /^(\+?\d[\d\s\-().]{7,})$/.test(value);
        case '_dob': {
          const fullDate = /^\d{4}-\d{2}-\d{2}$/;
          if (!fullDate.test(value)) return true; // still typing
          const dob = new Date(value);
          if (isNaN(dob.getTime())) return false;
          const today   = new Date();
          let age       = today.getFullYear() - dob.getFullYear();
          const mDiff   = today.getMonth() - dob.getMonth();
          if (mDiff < 0 || (mDiff === 0 && today.getDate() < dob.getDate())) age--;
          if (age < 16) {
            const dlg = document.getElementById('modal-errors');
            const bd  = dlg?.querySelector('.modal-body');
            if (bd) bd.textContent = window?.errorMessages?.cart_age;
            dlg?.classList.add('is-open');
            return false;
          }
          if (age > 99) return false;
          return true;
        }
        default:
          return true;
      }
    }

    /* ================= local update ================= */
    update (evt) {
      const target = evt.target;
      const { name } = target;
      if (!name || name === 'properties') return;
      if (target.type === 'file' && !target.files[0] && evt.type !== 'remove-file') return;
      const value = target.type === 'file' && target.files[0]
        ? target.files[0].name
        : (target.type === 'checkbox' ? target.checked : target.value);

      if (!this.validateInput(name, value)) {
        target.classList.add('invalid');
        return;
      }
      target.classList.remove('invalid');

      /* write to component + parent */
      if (target.type === 'file' && target.files[0]) {
        return new Promise(res => {
          const reader = new FileReader();
          reader.onload = () => {
            this.setProperty(name, reader.result);
            this.dispatchEvent(new CustomEvent('onInputSaved', { bubbles: true, detail: { name, value: reader.result } }));
            setFieldCache(this.data.variant, this.data.cacheId, name, reader.result);
            res();
          };
          reader.readAsDataURL(target.files[0]);
        });
      } else {
        this.setProperty(name, value);
        this.dispatchEvent(new CustomEvent('onInputSaved', { bubbles: true, detail: { name, value } }));
        setFieldCache(this.data.variant, this.data.cacheId, name, value);
        return Promise.resolve();
      }
    }

    /* ================= row actions ================= */
    setProperty (name, value) {
      const property = this.data.properties.find(([key]) => key === name);
      if (property) property[1] = value;
      else this.data.properties.push([name, value]);
      const fileInput = Array.from(this.selectors.fields || []).find(field => field.name === name && field.type === 'file');
      if (fileInput) fileInput.required = this.requiredFileFields.has(name) && !value;
    }

    getProperties () {
      const properties = this.propertiesToObject(this.data.properties);
      this.selectors.fields?.forEach(field => {
        if (field.type !== 'file') {
          properties[field.name] = field.type === 'checkbox' ? field.checked : field.value;
        }
      });
      const storedIndex = properties._delegate_index;
      properties._delegate_index = storedIndex !== undefined && storedIndex !== null && storedIndex !== '' && !isNaN(Number(storedIndex))
        ? Number(storedIndex)
        : parseInt(this.data.delegate.split('_').pop(), 10) - 1;
      return properties;
    }

    delete () {
      if (this.processing) return; this.processing = true;
      const { line, delegate } = this.data;
      this.dispatchEvent(new CustomEvent('onParticipantRemoved', { bubbles: true, detail: { line, delegate } }));
      this.processing = false;
    }

    add () {
      if (this.processing) return; this.processing = true;
      
      // Check if variant has inventory available (inventory - already in cart)
      const addButton = this.selectors.add;
      const variantInventory = parseInt(addButton?.dataset.variantInventory, 10);
      const cartQuantity = parseInt(addButton?.dataset.cartQuantity, 10);
      const availableToAdd = variantInventory - cartQuantity;
      
      if (availableToAdd <= 0 || isNaN(availableToAdd)) {
        console.log('No more variants available - inventory:', variantInventory, 'in cart:', cartQuantity, 'available:', availableToAdd);
        // Show error modal
        const dlg = document.getElementById('modal-errors');
        const bd  = dlg?.querySelector('.modal-body');
        if (bd) bd.textContent = window?.errorMessages?.cart_no_space || 'There is no more space available for this course.';
        dlg?.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        this.processing = false;
        return;
      }
      
      const { line, delegate } = this.data;
      this.dispatchEvent(new CustomEvent('onParticipantAdded', { bubbles: true, detail: { line, delegate } }));
      this.processing = false;
      console.log('Participant added:', { line, delegate });
    }

    /* util */
    propertiesToObject (arr = []) {
      return arr.reduce((o, [k, v]) => {
        if (k !== 'properties') o[k] = v;  // skip accidental nested key
        return o;
      }, {});
    }
  });
}

/* =========================
   cart-participants
   ========================= */
if (!customElements.get('cart-participants')) {
  customElements.define('cart-participants', class CartParticipants extends HTMLElement {
    constructor () {
      super();
      this.processing = false;
      this.rows       = Array.from(this.querySelectorAll('cart-participant-row'));
      this.bookingCheckbox = this.querySelector('#booking');
      this.resultsCheckbox = this.querySelector('#results');
      this.form            = this.closest('form#cart');

      /* snapshot keyed by line index */
      this.cart = Object.values(this.rows.reduce((acc, row) => {
        const d = row.data;
        if (!acc[d.line]) acc[d.line] = { line: d.line, variant: d.variant, participants: [] };
        const props = JSON.parse(JSON.stringify(d.properties || []));
        console.log('Row properties:', props);
        
        // Ensure _delegate_index exists and is numeric
        const existingIndexProp = props.find(([k]) => k === '_delegate_index');
        let hasValidIndex = false;
        if (existingIndexProp && existingIndexProp[1] !== undefined && existingIndexProp[1] !== null && existingIndexProp[1] !== '') {
          const parsed = parseInt(existingIndexProp[1], 10);
          if (!isNaN(parsed)) {
            hasValidIndex = true;
          }
        }
        
        if (!hasValidIndex) {
          // Extract numeric delegate index from composite key (e.g., "0_2" -> 1)
          let delegateIndex = 0;
          if (typeof d.delegate === 'string' && d.delegate.includes('_')) {
            const parts = d.delegate.split('_');
            delegateIndex = parseInt(parts[parts.length - 1], 10) - 1; // Convert from 1-indexed to 0-indexed
          }
          // Remove existing invalid index if present
          if (existingIndexProp) {
            props.splice(props.indexOf(existingIndexProp), 1);
          }
          props.push(['_delegate_index', delegateIndex]);
        }
        
        acc[d.line].participants.push({
          id: d.variant,
          properties: props,
          delegate: d.delegate,
        });
        return acc;
      }, {}));

      this.cartAttributes = { booking: this.bookingCheckbox?.checked || false, results: this.resultsCheckbox?.checked || false };

      /* child events */
      this.addEventListener('onInputSaved',        this.onInputSaved.bind(this));
      this.addEventListener('onParticipantRemoved', this.onParticipantRemoved.bind(this));
      this.addEventListener('onParticipantAdded',   this.onParticipantAdded.bind(this));

      //physical item removal
      this.querySelectorAll('.physical_item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          // Find the closest cart-item
          const cartItem = item.closest('.cart-item');
          if (!cartItem) return;

          // Find the .cart-item__remove-button inside cartItem and get its variant-id attribute
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : null;
          if(!variantId) return;
          // Send a request to remove the item from the cart
            fetch(`${routes.cart_change_url}`, {
                method : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body   : JSON.stringify({ quantity: 0, id: variantId, sections: 'default-cart' })
              }).then(() => window.location.reload());
              this.selectors.cartItem?.remove();
        });
      });

      // quantity inputs for physical items
      this.querySelectorAll('quantity-input').forEach(qtyInput => {
        const input = qtyInput.querySelector('.quantity__input');
        const minusBtn = qtyInput.querySelector('button[name="minus"]');
        const plusBtn = qtyInput.querySelector('button[name="plus"]');
        console.debug('quantity-input', qtyInput, input, minusBtn, plusBtn);
        if (!input) return;

        // Helper to update quantity in Shopify cart and refresh UI
        const updateCartQuantity = (newQty) => {
          // Find the closest cart-item-row or cart-item to get the variant id
          const cartItemRow = qtyInput.closest('cart-item-row');
          if (!cartItemRow) return;
          const removeButton = cartItemRow.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItemRow.getAttribute('variant-id');
          if (!variantId) return;

          fetch(`${routes.cart_change_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: newQty, id: variantId, sections: 'default-cart' })
          }).then(() => window.location.reload());
        };
          minusBtn?.addEventListener('click', () => {
            let current = parseInt(input.value, 10) || 0;
            console.debug('minusBtn clicked, current quantity:', current);
              if (current >= 1) {
              updateCartQuantity(current);
              }else if (current < 1) {
                const cartItemRow = qtyInput.closest('cart-item-row');
                if (!cartItemRow) return;
                const removeButton = cartItemRow.querySelector('.cart-item__remove-button');
                const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItemRow.getAttribute('variant-id');
                if (!variantId) return;

                fetch(`${routes.cart_change_url}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ quantity: 0, id: variantId, sections: 'default-cart' })
                }).then(() => window.location.reload());
              }
          });
          plusBtn?.addEventListener('click', () => {
            let current = parseInt(input.value, 10) || 1;
            updateCartQuantity(current);
          });
      });

      /* attribute checkboxes */
      this.bookingCheckbox?.addEventListener('change', () => { this.cartAttributes.booking = this.bookingCheckbox.checked; });
      this.resultsCheckbox?.addEventListener('change', () => { this.cartAttributes.results = this.resultsCheckbox.checked; });

      /* form submit */
      this.form?.addEventListener('submit', async e => {
        e.preventDefault();

        await Promise.all(this.rows.map(r => r.flush()));
        const invalid = this.querySelectorAll('.invalid');
        if (invalid.length) { invalid[0].focus(); return; }

        // Validate unique names before proceeding
        if (!this.validateUniqueNames()) return;
        
        // Validate unique emails before proceeding
        if (!this.validateUniqueEmails()) return;

        this.buildCartSnapshot();

        await this.saveChanges('redirect');
      });
      
    }

     validateUniqueNames() {
    // Map: line index (cart-item) -> Set of name keys
    const lineNameMap = {};
    let hasDuplicate = false;
    let duplicateName = '';
    let duplicateLine = null;

    this.rows.forEach(row => {
      const firstNameInput = row.querySelector('[name="_first_name"]');
      const lastNameInput = row.querySelector('[name="_last_name"]');
      const firstName = firstNameInput ? firstNameInput.value.trim() : '';
      const lastName = lastNameInput ? lastNameInput.value.trim() : '';
      if (!firstName || !lastName) return; // skip empty

      const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
      const line = row.data.line;

      if (!lineNameMap[line]) lineNameMap[line] = new Set();
      if (lineNameMap[line].has(key)) {
        hasDuplicate = true;
        duplicateName = `${firstName} ${lastName}`;
        duplicateLine = line;
      }
      lineNameMap[line].add(key);
    });

console.debug('Unique name validation:', lineNameMap, hasDuplicate, duplicateName);

      if (hasDuplicate) {
        const dlg = document.getElementById('modal-errors');
        const body = dlg?.querySelector('.modal-body');
        if (body) body.textContent = `You have entered this delegate twice: ${duplicateName}.\nPlease update with the correct details for each delegate.`;
        dlg?.classList.add('is-open');
        return false;
      }
      return true;
    }

    validateUniqueEmails() {
      // Map: line index (cart-item) -> Set of email keys
      const lineEmailMap = {};
      let hasDuplicate = false;
      let duplicateEmail = '';
      let duplicateLine = null;

      this.rows.forEach(row => {
        const emailInput = row.querySelector('[name="_email"]');
        const email = emailInput ? emailInput.value.trim() : '';
        if (!email) return; // skip empty

        const key = email.toLowerCase();
        const line = row.data.line;

        if (!lineEmailMap[line]) lineEmailMap[line] = new Set();
        if (lineEmailMap[line].has(key)) {
          hasDuplicate = true;
          duplicateEmail = email;
          duplicateLine = line;
        }
        lineEmailMap[line].add(key);
      });

      console.debug('Unique email validation:', lineEmailMap, hasDuplicate, duplicateEmail);

      if (hasDuplicate) {
        const dlg = document.getElementById('modal-errors');
        const body = dlg?.querySelector('.modal-body');
        if (body) body.textContent = `You have entered this email twice: ${duplicateEmail}.\nPlease update with the correct email for each delegate.`;
        dlg?.classList.add('is-open');
        return false;
      }
      return true;
    }


    buildCartSnapshot () {

        this.cart = Array.from(this.querySelectorAll('cart-participant-row')).map(row => {
        const d = row.data;
        
        // Extract numeric delegate index from composite key (e.g., "0_2" -> 1)
        let delegateIndex = 0;
        if (typeof d.delegate === 'string' && d.delegate.includes('_')) {
          const parts = d.delegate.split('_');
          delegateIndex = parseInt(parts[parts.length - 1], 10) - 1; // Convert from 1-indexed to 0-indexed
        } else if (!isNaN(parseInt(d.delegate, 10))) {
          delegateIndex = parseInt(d.delegate, 10);
        }
        
        return {
          line: d.line,
          variant: d.variant,
          key: row.dataset.lineKey, // key
          participants: [{
            id: d.variant,
            delegate: d.delegate,
            properties: Object.entries(row.getProperties())
          }]
        };
      });
        // --- Add physical items that are not participants ---
      const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
      physicalItems.forEach(cartItem => {
        // Get variantId from .cart-item__remove-button or data attribute
        const removeButton = cartItem.querySelector('.cart-item__remove-button');
        const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
        const qtyEl = cartItem.querySelector('.quantity__input');
        console.debug('Processing physical item:', qtyEl);
        let quantity = 1;
            if (qtyEl) {
              quantity = parseInt(qtyEl.value, 10) || 1;
        }
        // Check if already in cart snapshot (by variant)
        const alreadyInCart = this.cart.some(item => item.variant == variantId);
        if (variantId && !alreadyInCart) {
          this.cart.push({
            line: null,
            variant: variantId,
            quantity: quantity,
            participants: []
            
          });
          console.debug('Added non-participant physical item to cart:', variantId);
        }
      });
    }

    propertiesToObject (arr = []) {
      return arr.reduce((o, [k, v]) => {
        if (k !== 'properties') o[k] = v;  // skip accidental nested key
        return o;
      }, {});
    }

    /* ================= child events ================= */
    onInputSaved (e) {
      const { name, value }   = e.detail;
      const { line, delegate } = e.target.data;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      const p    = item.participants.find(p => p.delegate === delegate); if (!p) return;
      
      // Ensure _delegate_index exists and is numeric
      const existingIndexProp = p.properties.find(([k]) => k === '_delegate_index');
      let hasValidIndex = false;
      if (existingIndexProp && existingIndexProp[1] !== undefined && existingIndexProp[1] !== null && existingIndexProp[1] !== '') {
        const parsed = parseInt(existingIndexProp[1], 10);
        if (!isNaN(parsed)) {
          hasValidIndex = true;
        }
      }
      
      if (!hasValidIndex) {
        // Extract numeric delegate index from composite key
        let delegateIndex = 0;
        if (typeof delegate === 'string' && delegate.includes('_')) {
          const parts = delegate.split('_');
          delegateIndex = parseInt(parts[parts.length - 1], 10) - 1;
        }
        // Remove existing invalid index if present
        if (existingIndexProp) {
          p.properties.splice(p.properties.indexOf(existingIndexProp), 1);
        }
        p.properties.push(['_delegate_index', delegateIndex]);
      }

      const idx = p.properties.findIndex(([k]) => k === name);
      idx > -1 ? p.properties[idx][1] = value : p.properties.push([name, value]);
    }

    /*async onParticipantRemoved (e) {
      const { line, delegate } = e.detail;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      item.participants = item.participants.filter(p => p.delegate !== delegate);
      if (!item.participants.length) this.cart = this.cart.filter(c => c.line !== line);
      await this.saveChanges('reload');
    }*/
   /*async onParticipantRemoved(e) {
      const { line, delegate } = e.detail;
      const item = this.cart.find(c => c.line === line); 
      if (!item) return;

      item.participants = item.participants.filter(p => p.delegate !== delegate);
      console.log("remove", item.participants)

        // Find the cart-participant-row for this line and delegate
        const row = this.rows.find(r => r.data.line == line && r.data.delegate == delegate);
        const lineKey = row?.dataset.rowKey;
        console.log("lineKey", lineKey, row)

        // Fallback: Try to find the line key from window.cart.items if not found
        let finalLineKey = lineKey;
        if (!finalLineKey && window.cart && window.cart.items) {
          const delegateProps = getDelegateCache(row.data.variant, row.data.cacheId) || {};
          finalLineKey = window.cart.items.find(cartItem =>
            cartItem.variant_id == item.variant &&
            cartItem.properties &&
            Object.entries(delegateProps).every(([k, v]) => cartItem.properties[k] == v)
          )?.key;
        }
        console.log("finalLineKey", finalLineKey)
      //if (!item.participants.length) {
        // Remove the item from the cart in Shopify
        console.log("remove item", item)
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: finalLineKey, quantity: 0 })
        });
        // Remove from local cart snapshot
        this.cart = this.cart.filter(c => c.line !== line);
        window.location.reload(); // Optionally refresh the cart UI
        return;
      
      //window.location.reload();
      //await this.saveChanges('reload');
    //}
    }*/

    async onParticipantRemoved(e) {
      const { line, delegate } = e.detail;
      const row = this.rows.find(row => row.data.line === line && row.data.delegate === delegate);
      if (!row) return;
      if (!await this.saveChanges()) return;
      this.process(true);
      try {
        await this.cartRequest('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.dataset.rowKey, quantity: 0 })
        });
        const cart = await this.cartRequest('/cart.js');
        const remainingVariants = cart.items
          .filter(item => item.properties?._source !== 'Rebuy')
          .map(item => String(item.variant_id));
        const rebuyItemsToRemove = cart.items.filter(item => {
          if (item.properties?._source !== 'Rebuy') return false;
          const linkedVariants = (item.properties._linked_to_variants || '').split(',').map(variant => variant.trim()).filter(Boolean);
          return !linkedVariants.some(variant => remainingVariants.includes(variant));
        });
        for (const item of rebuyItemsToRemove) {
          await this.cartRequest('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
        }
        window.location.reload();
      } catch (error) {
        this.showError(error);
      } finally {
        this.process(false);
      }
    }

    async onParticipantAdded (e) {
      const { line } = e.detail;
      const item = this.cart.find(c => c.line === line); if (!item) return;
      if (!await this.saveChanges()) return;
      const newDel = Math.max(-1, ...this.rows
        .filter(row => row.data.variant === item.variant)
        .map(row => row.getProperties()._delegate_index)) + 1;
      this.process(true);
      try {
        await this.cartRequest('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: item.variant,
              quantity: 1,
              properties: { _delegate_index: newDel, _uuid: crypto.randomUUID() }
            }]
          })
        });
        window.location.reload();
      } catch (error) {
        this.showError(error);
      } finally {
        this.process(false);
      }
    }

    /* ================= persistence ================= */
    async saveChanges (action) {
      if (this.processing) return false;
      this.process(true);
      try {
        await Promise.all(this.rows.map(row => row.flush()));
        this.buildCartSnapshot();
        await this.persistChanges();
        clearAllCache();
        if (action === 'reload') window.location.reload();
        if (action === 'redirect') window.location.href = '/checkout';
        return true;
      } catch (error) {
        this.showError(error);
        return false;
      } finally {
        this.process(false);
      }
    }

    showError (error) {
      const dialog = document.getElementById('modal-errors');
      const body = dialog?.querySelector('.modal-body');
      if (body) body.textContent = error.message || window.cartStrings?.error;
      dialog?.classList.add('is-open');
    }

    async cartRequest (url, options) {
      const response = await fetch(url, options);
      const cart = await response.json();
      if (!response.ok || cart.status >= 400) {
        throw new Error(cart.description || cart.message || window.cartStrings?.error || response.statusText);
      }
      return cart;
    }

    async persistChanges () {
      /*try {
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (err) {
        console.error('clear cart err', err);
        this.process(false);
        return;
      }*/

      if (!this.rows.length) {
        // Only process physical items and attributes
        console.log('No participants, processing physical items only');
        const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
        for (const cartItem of physicalItems) {
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
          const qtyEl = cartItem.querySelector('.quantity__input');
          let quantity = 1;
          if (qtyEl) {
            quantity = parseInt(qtyEl.value, 10) || 1;
          }
          const lineKey = cartItem.dataset.lineKey;
          await this.cartRequest('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: lineKey || variantId,
              quantity: quantity
            })
          });
        }
        // Save attributes and proceed
        await this.saveAttributes();
        return;
      }

      const usedIndexes = new Map();
      const usedUuids = new Set();
      for (const row of this.rows) {
        const properties = row.getProperties();
        const indexes = usedIndexes.get(row.data.variant) || new Set();
        let index = properties._delegate_index;
        if (indexes.has(index)) {
          index = Math.max(...this.rows.filter(other => other.data.variant === row.data.variant)
            .map(other => other.getProperties()._delegate_index), ...indexes) + 1;
        }
        indexes.add(index);
        usedIndexes.set(row.data.variant, indexes);
        row.setProperty('_delegate_index', index);
        if (!properties._uuid || usedUuids.has(properties._uuid)) row.setProperty('_uuid', crypto.randomUUID());
        usedUuids.add(row.getProperties()._uuid);
      }

      for (const row of this.rows) {
        const properties = row.getProperties();
        const lineKey = row.dataset.rowKey;
        if (!lineKey && !row.data.needsAdd) throw new Error(window.cartStrings?.error);
        const siblings = this.rows.filter(other => other !== row && other.dataset.rowKey === lineKey && !other.data.needsAdd);
        const cart = await this.cartRequest(row.data.needsAdd ? '/cart/add.js' : '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(row.data.needsAdd
            ? { items: [{ id: row.data.variant, quantity: 1, properties }] }
            : { id: lineKey, quantity: 1, properties })
        });
        if (!row.data.needsAdd) siblings.forEach(other => { other.data.needsAdd = true; });
        const savedItem = cart.items.find(item => String(item.variant_id || item.id) === row.data.variant && item.properties?._uuid === properties._uuid);
        if (!savedItem?.key) throw new Error(window.cartStrings?.error);
        row.dataset.rowKey = savedItem.key;
        row.data.key = savedItem.key;
        row.data.quantity = 1;
        row.data.needsAdd = false;
        const cache = getDelegateCache(row.data.variant, row.data.cacheId);
        const cacheId = `${properties._uuid}:1`;
        if (cacheId !== row.data.cacheId && cache) {
          Object.entries(cache).forEach(([name, value]) => setFieldCache(row.data.variant, cacheId, name, value));
          removeDelegateCache(row.data.variant, row.data.cacheId);
        }
        row.data.cacheId = cacheId;
      }

      // --- Update physical items ---
        const physicalItems = Array.from(this.querySelectorAll('cart-item-row[data-physical="true"]'));
        for (const cartItem of physicalItems) {
          const removeButton = cartItem.querySelector('.cart-item__remove-button');
          const variantId = removeButton ? removeButton.getAttribute('variant-id') : cartItem.getAttribute('variant-id');
          const qtyEl = cartItem.querySelector('.quantity__input');
          let quantity = 1;
          if (qtyEl) {
            quantity = parseInt(qtyEl.value, 10) || 1;
          }
          // Try to get the line key if available
          const lineKey = cartItem.dataset.lineKey;
          await this.cartRequest('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: lineKey || variantId,
              quantity: quantity
            })
          });
        }

      /* save attributes */
      await this.saveAttributes();
    }

    async saveAttributes () {
      const fd = new FormData();
      fd.append('attributes[Booking confirmation - Booker & Delegate]', this.cartAttributes.booking);
      fd.append('attributes[Results - Booker & Delegate]', this.cartAttributes.results);
      await this.cartRequest('/cart/update.js', { method: 'POST', body: fd });
    }

    /* ================= UI helpers ================= */
    process (state) {
      this.processing = state;
      const btn = this.form?.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = state;
        btn.classList.toggle('btn--loading', state);
      }
    }
  });
}
